// signClient — PUBLIC (no-auth) client for the signer-facing signing page.
//
// Like intakeClient, this NEVER attaches a Bearer token: /api/sign/:uuid is
// public and gated solely by the uuid in the path (the signer has no login).
// Mirrors the wire shapes in apps/api/src/routes/leaseSign.ts (SignView).

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

export type SignState =
  | 'ready'
  | 'awaiting'
  | 'signed'
  | 'executed'
  | 'declined'
  | 'cancelled'
  | 'not_found'
  // The addressed document was cancelled AND replaced by a live lease for this
  // signer; `to` carries the replacement's SPA path. SignPage navigates there.
  | 'redirect';

/** A doc type the page has bespoke chrome for; 'document' is the generic fallback. */
export type SignDocumentType =
  | 'lease'
  | 'adoption_addendum'
  | 'condition_report'
  | 'moveout_funds_direction'
  | 'payment_plan'
  | 'manual_document'
  | 'property_management_agreement'
  | 'leaseholder_change'
  | 'leaseholder_removal'
  | 'animal_addendum'
  | 'animal_addendum_paid'
  | 'w9'
  // The City of Walla Walla utilities agreement (the City's form, one owner signer).
  | 'city_utility_agreement'
  | 'document';

export interface SignParty {
  /**
   * A signer's full name, or the literal "Welcome Home Properties" for the
   * collapsed WHP party shown to tenant viewers. WHP-side viewers (WEL-29) get
   * each WHP signer listed individually by name instead.
   */
  label: string;
  sublabel?: string;
  status: 'signed' | 'awaiting';
  signedAt?: string;
  you?: boolean;
}

// Staff review summary — mirrors apps/api/src/lib/buildLeaseSignSummary.ts.
// Present on SignView ONLY for WHP staff (a WHP role, or a welcomehps.com
// signer email) viewing a lease in a state whose page shows the document
// (ready / awaiting / signed / executed); recomputed server-side from the
// frozen version snapshot the printed PDF was rendered from. The builder's
// preview uses the same shape (leaseDraftsClient.getDraftReviewSummary).

export interface ReviewSummaryRow {
  label: string;
  value: string;
  /** Render bold — totals, the RRA hold, anything the countersigner must not miss. */
  emphasis?: boolean;
}

export interface ReviewSummarySection {
  title: string;
  rows: ReviewSummaryRow[];
}

export interface ReviewSummary {
  /** The PDF version the summary was computed from (audit). */
  versionId?: string;
  /** When that version was generated (ISO-8601). */
  generatedAt?: string;
  sections: ReviewSummarySection[];
}

export interface SignView {
  state: SignState;
  /**
   * Present ONLY when state === 'redirect': the SPA-relative path of the
   * replacement lease's signing page (`/sign/<uuid>`). A redirect payload
   * carries nothing else — the page must navigate before reading other fields.
   */
  to?: string;
  documentType: SignDocumentType;
  /** Staff-provided document name for a manual_document (names the page chrome). */
  documentLabel?: string;
  /**
   * w9 only (owner-sync spec §6): who the W-9 is for. 'vendor' switches the page
   * to the vendor copy; absent or 'owner' keeps the owner copy.
   */
  w9RecipientKind?: 'owner' | 'vendor';
  property?: string;
  termStart?: string;
  termEnd?: string;
  residentNames?: string;
  /**
   * WEL-153 (animal_addendum_paid): the NEW animals this addendum adds, already
   * written as a sentence fragment by the server ("Mochi", "Mochi and Pip") so
   * the signing page, the emails and the document name them identically. Absent
   * for every other document type — and the intro degrades to a name-free
   * sentence rather than printing a placeholder.
   */
  animalNames?: string;
  /**
   * city_utility_agreement only: the owner's City account number is already printed
   * on the form, so the intro drops "leave it blank if you don't have it handy".
   */
  cityUtilityAccountNoPrinted?: boolean;
  /**
   * condition_report only: a CORRECTED copy of the report, viewed by a resident —
   * the intro says why a second copy exists and that signing it replaces the first.
   */
  correctedVersion?: boolean;
  /** With `correctedVersion`: the "what changed" note staff wrote for the residents. */
  correctionNote?: string;
  signerFirstName?: string;
  signerRole?: string;
  declinedByYou?: boolean;
  embedSrc?: string;
  parties: SignParty[];
  download: { available: boolean };
  /** WHP-staff-only review panel (see ReviewSummary above). */
  reviewSummary?: ReviewSummary;
  /**
   * The owner's contiguous post-EPMA flow (docs/city-utility-agreement-plan.md §4):
   * the next document THIS signer still has to sign (addendum → City utilities
   * agreement → W-9). `documentLabel` reads after "Next: "; `url` is an SPA-relative
   * `/sign/<uuid>` path. Absent when nothing is pending.
   */
  nextStep?: SignNextStep;
}

/** One step of the owner flow the page can continue to (mirrors the API's SignNextStep). */
export interface SignNextStep {
  documentLabel: string;
  url: string;
}

export class SignApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`Sign API error ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // No Authorization header — the uuid in the path is the only credential.
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new SignApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/** Fetch the signer's live state for a uuid. */
/** Fetch the signer's live state. Pass `{ poll: true }` for the SPA's background
 *  status re-polls so the server does NOT log a page_opened for them — only a
 *  genuine open (the initial landing) is an "opened" event. */
export function getSign(uuid: string, opts?: { poll?: boolean }): Promise<SignView> {
  const q = opts?.poll ? '?poll=1' : '';
  return request<SignView>(`/api/sign/${encodeURIComponent(uuid)}${q}`);
}

/** The download URL for the current-state document (302s to a presigned PDF).
 *  Used as a plain anchor href so the browser follows the redirect to S3. */
export function signDocumentUrl(uuid: string): string {
  return `${API_BASE}/api/sign/${encodeURIComponent(uuid)}/document`;
}
